export const getName = (type: string | undefined, node: any) => {
  switch (type) {
    case "field":
      return (node?.submodelName ? `${node.submodelName}. ${node?.name}` : node?.name)
    case "category":
      return (node?.submodelName ? `${node.submodelName}. ${node?.name}` : node?.name)
    case "choice":
      return `${node.submodelName}. ${node?.fieldName}. ${node?.name}`
    case "product":
      return `${node.submodelName}. ${node?.categoryName}. ${node?.name}`
    default:
      return node?.name
  }
}

export const getDisplayName = (node: any, modelData: any): string => {
  if (!node) return '';

  if (node.type === "submodel") {
    const model = modelData?.dataModels?.find(
      (model: any) => model?.id === node?.submodel || model.data?.id === node?.submodel
    );
    return model?.data?.name || '';
  }

  const result = {
    modelName: '',
    fieldName: '',
    categoryName: '',
    choiceName: '',
    productName: ''
  };

  if (modelData?.dataModels) {
    if (node.submodel) {
      const submodel = modelData.dataModels.find(
        (m: any) => m.id === node.submodel
      );
      if (submodel) {
        result.modelName = submodel.data?.name || '';
      }
    }

    for (const model of modelData.dataModels) {
      const modelDataObj = model?.data || {};
      let foundData = false;

      if (node.model === model.id) {
        result.modelName = modelDataObj.name || '';
        foundData = true;
      }

      if (node.field && !result.fieldName && modelDataObj.fields) {
        const field = modelDataObj.fields.find((f: any) => f.id === node.field);
        if (field) {
          result.fieldName = field.name || '';
          if (!result.modelName) result.modelName = modelDataObj.name || '';
          foundData = true;

          // For choice type, find the choice name from field's choices
          if (node.type === "choice" && field.dataConfiguration?.choices) {
            const choice = field.dataConfiguration.choices.find((c: any) => c.av_id === node.id);
            if (choice) {
              result.choiceName = choice.display_value || choice.av_value || '';
            }
          }
        }
      }

      if (node.category && !result.categoryName && modelDataObj.productCategories) {
        const category = modelDataObj.productCategories.find((c: any) => c.id === node.category);
        if (category) {
          // Look for category name in category data
          if (category.data?.name) {
            result.categoryName = category.data.name;
          } else if (category.data?.categories?.data) {
            // Try to find in nested categories.data structure
            const nestedCategory = category.data.categories.data.find(
              (c: any) => c.id === node.category
            );
            if (nestedCategory) {
              result.categoryName = nestedCategory.name || '';
            }
          }

          if (!result.modelName) result.modelName = modelDataObj.name || '';
          foundData = true;

          // For product type, find product in category
          if (node.type === "product" && category.data?.products) {
            const product = category.data.products.find((p: any) => p.id === node.id);
            if (product) {
              result.productName = product.name || product.display_value || '';
            }
          }
        }
      }

      // Look for node itself in models (for choices, products, categories, fields)
      if (node.id && !foundData) {
        // Search through fields to find the choice
        if (node.type === "choice" && modelDataObj.fields) {
          for (const field of modelDataObj.fields) {
            if (field.dataConfiguration?.choices) {
              const choice = field.dataConfiguration.choices.find((c: any) => c.av_id === node.id);
              if (choice) {
                result.choiceName = choice.display_value || choice.av_value || '';
                result.fieldName = field.name || '';
                if (!result.modelName) result.modelName = modelDataObj.name || '';
                foundData = true;
                break;
              }
            }
          }
        }

        // Search through product categories to find the product
        if (node.type === "product" && !foundData && modelDataObj.productCategories) {
          for (const category of modelDataObj.productCategories) {
            if (category.data?.products) {
              const product = category.data.products.find((p: any) => p.id === node.id);
              if (product) {
                result.productName = product.name || product.display_value || '';
                result.categoryName = category.data.name || '';
                if (!result.modelName) result.modelName = modelDataObj.name || '';
                foundData = true;
                break;
              }
            }
          }
        }

        // If node is itself a category
        if (!foundData && modelDataObj.productCategories) {
          const category = modelDataObj.productCategories.find((c: any) => c.id === node.id);
          if (category) {
            result.categoryName = category.data?.name || '';
            if (!result.modelName) result.modelName = modelDataObj.name || '';
            foundData = true;
          }
        }

        // If node is itself a field
        if (!foundData && modelDataObj.fields) {
          const field = modelDataObj.fields.find((f: any) => f.id === node.id);
          if (field) {
            result.fieldName = field.name || '';
            if (!result.modelName) result.modelName = modelDataObj.name || '';
            foundData = true;
          }
        }
      }

      // Break early if we found what we need based on node type
      if (foundData &&
        (node.type !== "field" || result.fieldName) &&
        (node.type !== "category" || result.categoryName) &&
        (node.type !== "choice" || result.choiceName) &&
        (node.type !== "product" || result.productName)) {
        break;
      }
    }
  }

  // Fall back to node properties only if modelData lookup failed
  if (node.type === "category" && !result.categoryName) {
    result.categoryName = node.name || '';
  } else if (node.type === "field" && !result.fieldName) {
    result.fieldName = node.name || '';
  }

  // Apply formatting based on type
  switch (node.type) {
    case "field":
      return result.modelName ? `${result.modelName}. ${result.fieldName}` : result.fieldName;
    case "category":
      return result.modelName ? `${result.modelName}. ${result.categoryName}` : result.categoryName;
    case "choice":
      return `${result.modelName}. ${result.fieldName}. ${result.choiceName !== '' ? result.choiceName : node?.name}`;
    case "product":
      return `${result.modelName}. ${result.categoryName}. ${result.productName !== '' ? result.productName : node?.name}`;
    default:
      return result.modelName && result.fieldName ?
        `${result.modelName}. ${result.fieldName}` :
        result.modelName || result.fieldName || node.name || '';
  }
};

export const getType = (t: string | undefined, ft: string | undefined) => {
  return t === "field" ? ft : t
}


export const areObjectsIdentical = <T>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }
  const keys1 = Object.keys(obj1) as Array<keyof T>;
  const keys2 = Object.keys(obj2) as Array<keyof T>;
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key) || !areObjectsIdentical(obj1[key], obj2[key])) {
      return false;
    }
  }
  return true;
};